import { useEffect, useState } from 'react';
import type Uppy from '@uppy/core';

import type { Id } from '@e-socna/data-layer';

import { useUppyUploadContext } from '../../../contexts/UppyUploadContext/hooks/useUppyUploadContext.hook';

interface UseUppyUploadInstanceParams {
  readonly entityId: Id;
  readonly fileCategoryId: Id;
  readonly tusCategory: string;
  readonly label?: string;
  readonly fileCategoryLabel?: string;
  readonly queryDocumentNamesToInvalidate: readonly string[];
}

export const useUppyUploadInstance = ({
  entityId,
  fileCategoryId,
  tusCategory,
  label,
  fileCategoryLabel,
  queryDocumentNamesToInvalidate,
}: Readonly<UseUppyUploadInstanceParams>) => {
  const { getUploadInstance, releaseUploadInstance } = useUppyUploadContext();
  // `uppy` starts null on first render so this hook keeps the same async-looking contract
  // as before, even though the instance itself now comes from the shared registry and is
  // never destroyed when this component unmounts - only created lazily, once, per scope.
  const [uppy, setUppy] = useState<Uppy | null>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setUppy(
      getUploadInstance({
        entityId,
        fileCategoryId,
        tusCategory,
        label,
        fileCategoryLabel,
        queryDocumentNamesToInvalidate,
      }),
    );
  }, [
    entityId,
    fileCategoryId,
    tusCategory,
    label,
    fileCategoryLabel,
    queryDocumentNamesToInvalidate,
    getUploadInstance,
  ]);

  // Deliberately keyed on the scope identity alone (not label/fileCategoryLabel, which can
  // change without leaving the scope) so this only fires when this hook actually stops
  // watching a given entity/category, not on every label update. releaseUploadInstance is a
  // no-op if the instance ended up with files added to it in the meantime.
  useEffect(
    () => () => {
      releaseUploadInstance({ entityId, fileCategoryId, tusCategory });
    },
    [entityId, fileCategoryId, tusCategory, releaseUploadInstance],
  );

  return uppy;
};
